import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, Calendar, Gift, Sparkles, Building2, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BENEFICIO_LABELS = {
  desconto_fixo: 'Desconto fixo (R$)',
  desconto_percentual: 'Desconto percentual (%)',
  brinde: 'Brinde',
  combo: 'Combo',
  frete_gratis: 'Frete grátis',
  produto_surpresa: 'Produto surpresa',
};

export default function MinhasCampanhas() {
  const [tab, setTab] = useState('disponiveis');
  const [disponiveis, setDisponiveis] = useState([]);
  const [adesoes, setAdesoes] = useState([]);
  const [meusNegocios, setMeusNegocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ negocio_id: '', beneficio_tipo: '', beneficio_descricao: '', beneficio_valor: '' });
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [d, a, mb] = await Promise.all([
        api.get('/v2/campanhas/disponiveis'),
        api.get('/v2/campanhas/minhas-adesoes'),
        api.get('/v2/coupons/my').catch(() => ({ data: [] })),
      ]);
      setDisponiveis(d.data?.items || []);
      setAdesoes(a.data?.items || []);
      // Lista negócios próprios via /v2/businesses minhas (simplificação: usa cupons.my e deduplica)
      // Melhor seria endpoint /v2/businesses/my mas /v2/coupons/my devolve business_id/negocio_nome.
      const seen = new Map();
      (mb.data || []).forEach((c) => {
        if (c.business_id && !seen.has(c.business_id)) {
          seen.set(c.business_id, { id: c.business_id, nome: c.negocio_nome });
        }
      });
      // Fallback: busca direta dos próprios negócios
      if (seen.size === 0) {
        try {
          const r = await api.get('/businesses', { params: { mine: true, limit: 100 } });
          (r.data?.items || r.data || []).forEach((b) => seen.set(b.id, { id: b.id, nome: b.nome }));
        } catch {}
      }
      setMeusNegocios(Array.from(seen.values()));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const adesoesPorCampanha = useMemo(() => {
    const m = new Set();
    adesoes.filter((a) => a.status === 'aprovada' || a.status === 'pendente')
      .forEach((a) => m.add(a.campanha_id));
    return m;
  }, [adesoes]);

  const openModal = (camp) => {
    setModal(camp);
    setForm({
      negocio_id: meusNegocios[0]?.id || '',
      beneficio_tipo: camp.beneficios_permitidos?.[0] || '',
      beneficio_descricao: '',
      beneficio_valor: '',
    });
  };

  const submit = async () => {
    if (!form.negocio_id) return toast.error('Selecione o negócio');
    if (!form.beneficio_tipo) return toast.error('Selecione o tipo de benefício');
    if ((form.beneficio_descricao || '').length < 3) return toast.error('Descreva o benefício');
    setSubmitting(true);
    try {
      const body = {
        negocio_id: form.negocio_id,
        beneficio_tipo: form.beneficio_tipo,
        beneficio_descricao: form.beneficio_descricao,
      };
      if (form.beneficio_valor !== '' && form.beneficio_valor !== null) {
        body.beneficio_valor = Number(form.beneficio_valor);
      }
      await api.post(`/v2/campanhas/${modal.id}/aderir`, body);
      toast.success('Você está participando! Seu cupom foi criado e já aparece na campanha.');
      setModal(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao aderir');
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (a) => {
    if (!window.confirm(`Cancelar adesão de "${a.negocio_nome}" à campanha?`)) return;
    try {
      await api.delete(`/v2/campanhas/${a.campanha_id}/aderir`, {
        params: { negocio_id: a.negocio_id },
      });
      toast.success('Adesão cancelada');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  return (
    <div className="space-y-4" data-testid="minhas-campanhas">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setTab('disponiveis')}
          data-testid="tab-disponiveis"
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            tab === 'disponiveis' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'
          }`}
        >
          Disponíveis {disponiveis.length > 0 && <span className="ml-1 opacity-80">({disponiveis.length})</span>}
        </button>
        <button
          onClick={() => setTab('minhas')}
          data-testid="tab-minhas"
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            tab === 'minhas' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'
          }`}
        >
          Minhas adesões {adesoes.length > 0 && <span className="ml-1 opacity-80">({adesoes.length})</span>}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Carregando…</div>
      ) : tab === 'disponiveis' ? (
        disponiveis.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-gray-500">
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            Nenhuma campanha disponível no momento. Volte em breve!
          </CardContent></Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {disponiveis.map((c) => {
              const aderiu = adesoesPorCampanha.has(c.id);
              return (
                <Card key={c.id} className="overflow-hidden" data-testid={`disponivel-${c.id}`}>
                  <div className="h-1.5 w-full" style={{ background: c.cor_destaque || '#6D28D9' }} />
                  {c.arte_visual_url && (
                    <img src={c.arte_visual_url} alt={c.nome} className="w-full h-32 object-cover" />
                  )}
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Megaphone className="w-4 h-4" style={{ color: c.cor_destaque || '#6D28D9' }} />
                        {c.nome}
                      </h3>
                      <Badge variant="outline">{c.tipo?.replace(/_/g, ' ')}</Badge>
                    </div>
                    {c.descricao && (
                      <p className="text-sm text-gray-600 line-clamp-2">{c.descricao}</p>
                    )}
                    <div className="text-xs text-gray-500 flex flex-wrap gap-3">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {String(c.data_inicio).slice(0, 10)} → {String(c.data_fim).slice(0, 10)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        +{c.pontos_irn_bonus} IRN
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(c.beneficios_permitidos || []).map((b) => (
                        <Badge key={b} className="bg-purple-50 text-purple-700">{BENEFICIO_LABELS[b] || b}</Badge>
                      ))}
                    </div>
                    <Button
                      data-testid={`aderir-${c.id}`}
                      onClick={() => openModal(c)}
                      disabled={aderiu || meusNegocios.length === 0}
                      className="w-full mt-2 bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
                    >
                      {aderiu ? 'Você já aderiu' : meusNegocios.length === 0 ? 'Sem negócios para aderir' : 'Aderir à campanha'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      ) : adesoes.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-gray-500">
          Você ainda não participa de nenhuma campanha.
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {adesoes.map((a) => (
            <Card key={a.id} data-testid={`adesao-${a.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold">{a.campanha?.nome || 'Campanha'}</h4>
                      <Badge>{a.status}</Badge>
                    </div>
                    <div className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                      <Building2 className="w-3 h-3" /> {a.negocio_nome}
                    </div>
                    <div className="text-sm text-gray-700 mt-2 flex items-center gap-2">
                      <Gift className="w-3 h-3 text-purple-600" />
                      {a.beneficio_descricao}
                    </div>
                    {a.cupom_id && (
                      <Link
                        to={`/cupom/${a.cupom_id}`}
                        className="text-xs text-purple-600 hover:underline inline-block mt-1"
                      >
                        Ver cupom →
                      </Link>
                    )}
                  </div>
                  {a.status === 'aprovada' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => cancel(a)}
                      data-testid={`cancelar-adesao-${a.id}`}
                      className="text-red-600"
                    >
                      <X className="w-3 h-3 mr-1" /> Cancelar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="aderir-modal">
          <DialogHeader>
            <DialogTitle>Aderir: {modal?.nome}</DialogTitle>
          </DialogHeader>
          {modal && (
            <div className="space-y-3">
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-3 text-sm">
                <p className="font-medium text-purple-900 mb-1">Regras da campanha</p>
                <ul className="text-xs text-purple-800 space-y-1 list-disc list-inside">
                  <li>Período: {String(modal.data_inicio).slice(0, 10)} → {String(modal.data_fim).slice(0, 10)}</li>
                  <li>Bônus IRN ao aderir: <strong>+{modal.pontos_irn_bonus}</strong></li>
                  {modal.validacao_obrigatoria && <li>Requer validação por PIN no balcão.</li>}
                </ul>
              </div>
              <div>
                <Label>Negócio</Label>
                <Select value={form.negocio_id} onValueChange={(v) => setForm({ ...form, negocio_id: v })}>
                  <SelectTrigger data-testid="modal-negocio"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {meusNegocios.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de benefício</Label>
                <Select value={form.beneficio_tipo} onValueChange={(v) => setForm({ ...form, beneficio_tipo: v })}>
                  <SelectTrigger data-testid="modal-beneficio-tipo"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {(modal.beneficios_permitidos || []).map((b) => (
                      <SelectItem key={b} value={b}>{BENEFICIO_LABELS[b] || b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descrição do benefício</Label>
                <Textarea
                  data-testid="modal-beneficio-descricao"
                  rows={2}
                  maxLength={300}
                  placeholder="Ex: 15% off em bolos personalizados"
                  value={form.beneficio_descricao}
                  onChange={(e) => setForm({ ...form, beneficio_descricao: e.target.value })}
                />
              </div>
              {(form.beneficio_tipo === 'desconto_fixo' || form.beneficio_tipo === 'desconto_percentual') && (
                <div>
                  <Label>Valor {form.beneficio_tipo === 'desconto_fixo' ? '(R$)' : '(%)'}</Label>
                  <Input
                    data-testid="modal-beneficio-valor"
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.beneficio_valor}
                    onChange={(e) => setForm({ ...form, beneficio_valor: e.target.value })}
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setModal(null)}>Cancelar</Button>
                <Button
                  data-testid="modal-confirmar"
                  onClick={submit}
                  disabled={submitting}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Confirmar adesão
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
